import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useURL } from '@/contexts/URLContext';
import { getOgImageForRoute } from '@/utils/ogImage';
import { getAlternateLanguageUrls, getCanonicalUrl, resolveSiteLanguage } from '@/utils/routing';
import { DOMAIN_TO_LANGUAGE, type Language } from '@/utils/languageSwitching';
import { getCurrencyConfig } from '@/utils/currency';
import { getPlanDisplayName, getPlanPrice } from '@/config/pricing';
import { BIDMIO_FOUNDING_YEAR } from '@/utils/copyright';
import {
  digitalShippingDetails,
  marketCountryFromHostname,
  marketCountryFromLanguage,
  marketDomainFromLanguage,
  merchantReturnPolicy,
  offerValidityWindow,
} from '@/utils/merchantListingOffer';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'product';
  structuredData?: object;
  noIndex?: boolean;
}

const languageToLocale: Record<string, string> = {
  sk: 'sk_SK',
  cs: 'cs_CZ',
  da: 'da_DK',
  en: 'en_US',
  de: 'de_DE',
};

const languageToCountry: Record<string, string> = {
  sk: 'SK',
  cs: 'CZ',
  da: 'DK',
  en: 'US',
  de: 'DE',
};

const languageToDomain: Record<string, string> = {
  sk: 'bidmio.sk',
  cs: 'bidmio.cz',
  da: 'bidmio.dk',
  en: 'bidmio.com',
  de: 'bidmio.de',
};

const languageToPlacename: Record<string, string> = {
  sk: 'Slovakia',
  cs: 'Czech Republic',
  da: 'Denmark',
  en: 'United States',
  de: 'Germany',
};

const languageToCoordinates: Record<string, { position: string; icbm: string }> = {
  sk: { position: '48.669026;19.699024', icbm: '48.669026, 19.699024' },
  cs: { position: '50.075538;14.437800', icbm: '50.075538, 14.437800' },
  da: { position: '55.676098;12.568337', icbm: '55.676098, 12.568337' },
  en: { position: '37.774929;-122.419418', icbm: '37.774929, -122.419418' },
  de: { position: '51.165691;10.451526', icbm: '51.165691, 10.451526' },
};

// English pages are written for the UK market (GBP, British rules).
const ENGLISH_MARKET = {
  locale: 'en_GB',
  country: 'GB',
  placename: 'United Kingdom',
  coordinates: { position: '51.507351;-0.127758', icbm: '51.507351, -0.127758' },
};

export const SEO = ({
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  structuredData,
  noIndex = false
}: SEOProps) => {
  const { i18n } = useTranslation();
  const currentLanguage = resolveSiteLanguage(i18n.language) || 'sk';
  const urlContext = useURL();
  
  const english = currentLanguage === 'en' ? ENGLISH_MARKET : null;
  const locale = english?.locale || languageToLocale[currentLanguage] || 'sk_SK';
  const country = english?.country || languageToCountry[currentLanguage] || 'SK';
  const placename = english?.placename || languageToPlacename[currentLanguage] || 'Slovakia';
  const coordinates = english?.coordinates || languageToCoordinates[currentLanguage] || languageToCoordinates.sk;
  const currentDomain = languageToDomain[currentLanguage] || 'bidmio.com';
  
  // Use URL from props, context (SSR), or fallback to window.location (client-side)
  const currentUrl = url || urlContext.fullUrl || (typeof window !== 'undefined' ? window.location.href : '');
  const currentPath = urlContext.pathname || (typeof window !== 'undefined' ? window.location.pathname : '/');
  const fullTitle = title.includes('Bidmio') ? title : `${title} | Bidmio`;
  
  // Auto-detect Replit development domains and block indexing
  const isReplitDomain = currentUrl.includes('replit.app') || currentUrl.includes('replit.dev') || 
                         (typeof window !== 'undefined' && (window.location.hostname.includes('replit.app') || window.location.hostname.includes('replit.dev')));
  const shouldNoIndex = noIndex || isReplitDomain;
  
  // Auto-detect page-specific OG image if not provided
  const ogImage = image || getOgImageForRoute(currentLanguage, currentPath);

  // Get all alternate language URLs
  const allAlternateUrls = getAlternateLanguageUrls(currentPath);
  
  // Filter hreflang based on current domain
  // .sk, .cz, .dk domains: show only native language + English
  // .com domain: show all 4 languages
  const currentHostname = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : currentDomain;
  const primaryLang = DOMAIN_TO_LANGUAGE[currentHostname] || currentLanguage as Language;
  
  const alternateUrls = currentHostname.includes('bidmio.com') 
    ? allAlternateUrls // .com shows all languages
    : allAlternateUrls.filter(u => u.lang === primaryLang || u.lang === 'en'); // Local domains show native + English
  
  // Get canonical URL for current language
  const canonicalUrl = url || getCanonicalUrl(currentPath, currentLanguage as 'sk' | 'cs' | 'da' | 'en' | 'de');

  // Convert relative image paths to absolute URLs for social media sharing
  const getAbsoluteImageUrl = (imgPath: string): string => {
    if (imgPath.startsWith('http://') || imgPath.startsWith('https://')) {
      return imgPath; // Already absolute
    }
    // Remove leading slash if present
    const cleanPath = imgPath.startsWith('/') ? imgPath.slice(1) : imgPath;
    return `https://${currentDomain}/${cleanPath}`;
  };

  const absoluteImageUrl = getAbsoluteImageUrl(ogImage);

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      {shouldNoIndex && <meta name="robots" content="noindex, nofollow" />}
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph Tags */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:image" content={absoluteImageUrl} />
      <meta property="og:image:secure_url" content={absoluteImageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={`${fullTitle} - Bidmio ERP`} />
      <meta property="og:site_name" content="Bidmio" />
      <meta property="og:locale" content={locale} />
      {import.meta.env.VITE_FACEBOOK_APP_ID && <meta property="fb:app_id" content={import.meta.env.VITE_FACEBOOK_APP_ID} />}

      {/* Twitter Cards */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={absoluteImageUrl} />
      <meta name="twitter:image:alt" content={`${fullTitle} - Bidmio ERP`} />

      {/* Additional Meta Tags */}
      <meta name="author" content="Bidmio" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta httpEquiv="Content-Language" content={currentLanguage} />
      <meta name="geo.region" content={country} />
      <meta name="geo.placename" content={placename} />
      <meta name="geo.position" content={coordinates.position} />
      <meta name="ICBM" content={coordinates.icbm} />
      
      {/* hrefLang links for all 4 domains */}
      {alternateUrls.map(({ lang, url }) => (
        <link key={lang} rel="alternate" hrefLang={lang} href={url} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={alternateUrls.find(u => u.lang === 'en')?.url || alternateUrls[0].url} />

      {/* Structured Data */}
      {structuredData && (
        Array.isArray(structuredData) ? (
          structuredData.map((data, index) => (
            <script key={index} type="application/ld+json">
              {JSON.stringify(data)}
            </script>
          ))
        ) : (
          <script type="application/ld+json">
            {JSON.stringify(structuredData)}
          </script>
        )
      )}
    </Helmet>
  );
};

// Predefined structured data schemas
const organizationDescriptions: Record<string, string> = {
  sk: "ERP pre slovenské stavebné firmy: projekty, dochádzka, CRM, sklady a fakturácia v jednom cloude. Dánska spoločnosť Sikmo Digital, slovenský zakladateľ.",
  cs: "ERP pro české stavební firmy: projekty, docházka, CRM, sklady a fakturace v jednom cloudu. Dánská společnost Sikmo Digital, lokalizováno pro Česko.",
  da: "ERP bygget til danske håndværkere: projekter, tidsregistrering, CRM, lager og fakturering i én cloud. Dansk virksomhed, Sikmo Digital.",
  en: "ERP for tradespeople in the EU: projects, time tracking, CRM, stock and invoicing in one cloud. Danish company Sikmo Digital, founded by a contractor.",
  de: "Handwerkersoftware für Handwerksbetriebe und Servicefirmen: Angebote, Aufträge, Zeiterfassung, Lager und Rechnungen in einer Cloud. Betrieben von Sikmo Digital, Dänemark.",
};

// Legal entity is Danish on every domain. Market pages use areaServed + local
// contact, not a fake local headquarters.
const LEGAL_ADDRESS = { country: "DK", locality: "Aarhus" };

const organizationAreaServed: Record<string, object | object[]> = {
  sk: { "@type": "Country", "name": "Slovakia" },
  cs: { "@type": "Country", "name": "Czech Republic" },
  da: { "@type": "Country", "name": "Denmark" },
  de: [
    { "@type": "Country", "name": "Germany" },
    { "@type": "Country", "name": "Austria" },
    { "@type": "Country", "name": "Switzerland" },
  ],
  en: [
    { "@type": "Country", "name": "Slovakia" },
    { "@type": "Country", "name": "Czech Republic" },
    { "@type": "Country", "name": "Denmark" },
    { "@type": "Country", "name": "United Kingdom" },
  ],
};

// Support languages per market site, so a country domain never advertises
// another market's language.
const supportLanguages: Record<string, string[]> = {
  sk: ["Slovak", "English"],
  cs: ["Czech", "English"],
  da: ["Danish", "English"],
  en: ["English"],
  de: ["English"],
};

export const createOrganizationSchema = (language?: string) => {
  const lang = resolveSiteLanguage(language || (typeof window !== 'undefined' ? document.documentElement.lang : 'sk')) || 'sk';
  const description = organizationDescriptions[lang] || organizationDescriptions.en;
  const address = LEGAL_ADDRESS;
  const domain = languageToDomain[lang] || 'bidmio.com';
  const areaServed = organizationAreaServed[lang] || organizationAreaServed.sk;

  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Bidmio",
    "url": `https://${domain}`,
    "logo": `https://${domain}/og-image.jpg`,
    "description": description,
    "foundingDate": String(BIDMIO_FOUNDING_YEAR),
    "industry": "Construction Technology",
    "areaServed": areaServed,
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": (lang === 'sk' || lang === 'cs') ? "+421 952 668 958" : "+45 71 75 73 91",
      "contactType": "Customer Service",
      "availableLanguage": supportLanguages[lang] || supportLanguages.en,
      "hoursAvailable": "Mo-Fr 09:00-17:00"
    },
    "address": {
      "@type": "PostalAddress",
      "addressCountry": address.country,
      "addressLocality": address.locality
    },
    "sameAs": [
      "https://www.facebook.com/bidmio",
      "https://www.linkedin.com/company/bidmio",
      "https://twitter.com/bidmio"
    ]
  };
};

const softwareApplicationDescriptions: Record<string, string> = {
  sk: "Cloudové ERP pre stavebné firmy: projektové riadenie, evidencia času, správa materiálu, CRM a finančný reporting v jednom systéme.",
  cs: "Cloudové ERP pro stavební firmy: projektové řízení, evidence času, správa materiálu, CRM a finanční reporting v jednom systému.",
  da: "Cloud-ERP til byggevirksomheder: projektstyring, tidsregistrering, materialestyring, CRM og økonomirapportering i ét system.",
  en: "Cloud ERP for construction firms: project management, time tracking, material control, CRM and financial reporting in one system.",
  de: "Cloud-Software für Handwerk und Bau: Angebote, Projektmanagement, Zeiterfassung, Materialverwaltung, CRM und Rechnungen in einem System.",
};

export const createSoftwareApplicationSchema = (language: string = 'sk') => {
  const { validFrom, priceValidUntil } = offerValidityWindow();
  const offerBase = {
    "@type": "Offer",
    validFrom,
    priceValidUntil,
    availability: "https://schema.org/InStock",
    billingIncrement: "Monthly",
  };
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Bidmio ERP",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web Browser",
    "description": softwareApplicationDescriptions[language] || softwareApplicationDescriptions.en,
    "url": "https://bidmio.com",
    "screenshot": "https://bidmio.com/og-image.jpg",
    "softwareVersion": "2.0",
    "datePublished": "2023-01-01",
    "dateModified": new Date().toISOString().split('T')[0],
    "author": {
      "@type": "Organization",
      "name": "Bidmio"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "ratingCount": "50",
      "bestRating": "5",
      "worstRating": "1"
    },
    "offers": [
      {
        ...offerBase,
        "name": getPlanDisplayName('solo', language),
        "price": String(getPlanPrice('solo', language)),
        "priceCurrency": getCurrencyConfig(language).code,
      },
      {
        ...offerBase,
        "name": getPlanDisplayName('pro', language),
        "price": String(getPlanPrice('pro', language)),
        "priceCurrency": getCurrencyConfig(language).code,
      },
      {
        ...offerBase,
        "name": getPlanDisplayName('developer', language),
        "price": String(getPlanPrice('developer', language)),
        "priceCurrency": getCurrencyConfig(language).code,
      }
    ],
    "featureList": [
      "Projektový manažment",
      "Správa materiálu",
      "Finančný reporting",
      "Sledovanie času",
      "CRM systém",
      "Mobilná aplikácia"
    ]
  };
};

export const createHowToSchema = (howto: {
  name: string;
  description: string;
  steps: Array<{ name: string; text: string }>;
}) => ({
  "@context": "https://schema.org",
  "@type": "HowTo",
  "name": howto.name,
  "description": howto.description,
  "step": howto.steps.map((step, index) => ({
    "@type": "HowToStep",
    "position": index + 1,
    "name": step.name,
    "text": step.text
  }))
});

export const createFAQSchema = (faqs: Array<{ question: string; answer: string }>) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
});

export const createBreadcrumbSchema = (breadcrumbs: Array<{ name: string; url: string }>) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  // Drop crumbs with non-http URLs (e.g. legacy "undefinedundefined") so Rich
  // Results never sees an invalid optional `item` / `@id`.
  "itemListElement": breadcrumbs
    .filter((breadcrumb) => /^https?:\/\//i.test(breadcrumb.url || ''))
    .map((breadcrumb, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": breadcrumb.name,
      "item": breadcrumb.url
    }))
});

export const createServiceSchema = (service: {
  name: string;
  description: string;
  url: string;
  image?: string;
  provider?: string;
}, language: string = 'sk') => ({
  "@context": "https://schema.org",
  "@type": "Service",
  "name": service.name,
  "description": service.description,
  "url": service.url,
  "image": service.image || "https://bidmio.com/og-image.jpg",
  "provider": {
    "@type": "Organization",
    "name": service.provider || "Bidmio"
  },
  "serviceType": "Construction ERP Software",
  "areaServed": organizationAreaServed[resolveSiteLanguage(language) || 'sk'] || organizationAreaServed.sk
});

export const createReviewSchema = (reviews: Array<{
  author: string;
  rating: number;
  text: string;
  date: string;
}>, language: string = 'sk') => {
  const averageRating = reviews.length > 0 
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : "5.0";
  
  const country = marketCountryFromLanguage(language);
  const { validFrom, priceValidUntil } = offerValidityWindow();
  const currency = getCurrencyConfig(language).code;
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : `https://${marketDomainFromLanguage(language)}`;
  
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "Bidmio ERP",
    "description": "Complete ERP system for construction companies with mobile apps and GPS tracking",
    "image": `${baseUrl}/og-image.jpg`,
    "brand": {
      "@type": "Brand",
      "name": "Bidmio"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": averageRating,
      "reviewCount": reviews.length,
      "bestRating": "5",
      "worstRating": "1"
    },
    "offers": {
      "@type": "Offer",
      "priceCurrency": currency,
      "price": String(getPlanPrice('pro', language)),
      "availability": "https://schema.org/InStock",
      "url": baseUrl,
      "validFrom": validFrom,
      "priceValidUntil": priceValidUntil,
      "shippingDetails": digitalShippingDetails(currency, country),
      "hasMerchantReturnPolicy": merchantReturnPolicy(country)
    },
    "review": reviews.map(review => ({
      "@type": "Review",
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": review.rating,
        "bestRating": "5"
      },
      "author": {
        "@type": "Person",
        "name": review.author
      },
      "reviewBody": review.text,
      "datePublished": review.date
    }))
  };
};

export const createPricingSchema = (plans: Array<{
  name: string;
  price: string;
  currency: string;
  description: string;
  features: string[];
}>, language?: string) => {
  const { validFrom, priceValidUntil } = offerValidityWindow();
  const hostname = typeof window !== 'undefined' ? window.location.hostname : undefined;
  const country = language
    ? marketCountryFromLanguage(language)
    : marketCountryFromHostname(hostname) ?? marketCountryFromLanguage();
  const baseUrl = typeof window !== 'undefined'
    ? window.location.origin
    : `https://${marketDomainFromLanguage(language)}`;
  
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "Bidmio ERP Software",
    "description": "Complete ERP system for construction companies with mobile apps and GPS tracking",
    "image": `${baseUrl}/og-image.jpg`,
    "brand": {
      "@type": "Brand",
      "name": "Bidmio"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "50",
      "bestRating": "5",
      "worstRating": "1"
    },
    "offers": plans.map(plan => ({
      "@type": "Offer",
      "name": plan.name,
      "price": plan.price,
      "priceCurrency": plan.currency,
      "description": plan.description,
      "availability": "https://schema.org/InStock",
      "validFrom": validFrom,
      "priceValidUntil": priceValidUntil,
      "url": baseUrl,
      "eligibleRegion": country,
      "shippingDetails": digitalShippingDetails(plan.currency, country),
      "hasMerchantReturnPolicy": merchantReturnPolicy(country),
      "itemOffered": {
        "@type": "SoftwareApplication",
        "name": plan.name,
        "featureList": plan.features
      }
    }))
  };
};

export const createWebPageSchema = (page: {
  name: string;
  description: string;
  url: string;
  breadcrumbs?: Array<{ name: string; url: string }>;
}) => ({
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": page.name,
  "description": page.description,
  "url": page.url,
  "isPartOf": {
    "@type": "WebSite",
    "name": "Bidmio",
    "url": "https://bidmio.com"
  },
  "breadcrumb": page.breadcrumbs ? createBreadcrumbSchema(page.breadcrumbs) : undefined,
  "mainEntity": {
    "@type": "SoftwareApplication",
    "name": "Bidmio ERP"
  }
});

export const createArticleSchema = (article: {
  headline: string;
  description: string;
  url: string;
  imageUrl?: string;
  datePublished: string;
  dateModified: string;
  author: string;
  keywords?: string[];
  language?: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": article.headline,
  "description": article.description,
  "url": article.url,
  "image": article.imageUrl || "https://bidmio.com/og-image.jpg",
  "datePublished": article.datePublished,
  "dateModified": article.dateModified,
  "author": {
    "@type": "Organization",
    "name": article.author || "Bidmio"
  },
  "publisher": {
    "@type": "Organization",
    "name": "Bidmio",
    "logo": {
      "@type": "ImageObject",
      "url": "https://bidmio.com/og-image.jpg"
    }
  },
  "mainEntityOfPage": {
    "@type": "WebPage",
    "@id": article.url
  },
  "keywords": article.keywords?.join(', ') || '',
  "articleSection": "Business Technology",
  "inLanguage": article.language || "sk"
});

export const createVideoObjectSchema = (video: {
  name: string;
  description: string;
  thumbnailUrl: string;
  embedUrl: string;
  pageUrl: string;
  uploadDate?: string;
  duration?: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "VideoObject",
  "name": video.name,
  "description": video.description,
  "thumbnailUrl": video.thumbnailUrl,
  "embedUrl": video.embedUrl,
  "url": video.pageUrl,
  ...(video.uploadDate ? { "uploadDate": video.uploadDate } : {}),
  ...(video.duration ? { "duration": video.duration } : {}),
  "publisher": {
    "@type": "Organization",
    "name": "Bidmio"
  }
});